import type { AxiosInstance } from "axios";
import { staffApi, vaultActorApi } from "./index";

/**
 * BDC / NIP data layer.
 *
 * Las mutaciones del flujo nativo BDC son ACTOR-ONLY: viven dentro del flujo
 * `/vault`, donde el único actor es el inquilino/aval autenticado con el JWT
 * del vault (`Bearer <jwtToken>` en sessionStorage). El personal de
 * operaciones nunca dispara, confirma ni reintenta el NIP.
 *
 * Las lecturas read-only (`status` y `company-data`) también se exponen con
 * `staffBdcApi`, cableado al cliente del token de sesión staff, para que `/folders` no
 * caiga en un falso error de autorización. Ese cliente no se usa para ninguna
 * mutación.
 *
 * Llama al PROPIO backend (`mb-multiexpediente`, app `docvault`) usando la
 * instancia axios AUTENTICADA del actor del vault — la MISMA `vaultActorApi`
 * que `vault.api.ts`. NO usa un cliente crudo, NO habla con mb_platform
 * directo, NO hay CORS.
 *
 * Prefijo (VITE_API_HOST ya incluye `/api/v1`):
 *   {VITE_API_HOST}/docvault/requirements/<requirement_id>/bdc/...
 * donde <requirement_id> = id (UUID) del Requirement `bdc-authorization`.
 *
 * Invariantes del contrato:
 *  - El cliente NUNCA envía `credit_report_id` (se resuelve server-side).
 *  - El cliente NUNCA envía `is_guarantor` (se deriva del slug del subfolder).
 *
 */

/** Estados que puede devolver el flujo BDC (campo `status`). */
export type BdcStatus =
  | "capturing_inputs"
  | "pin_validation"
  | "completed"
  | "long_report"
  | "in_review"
  | "recovering"
  | "invalid";

export type BdcDialCode = "+52" | "+1";

/** Métodos de reenvío del NIP. */
export type BdcResendMethod = "Email" | "SMS";

/** `legal_type` crudo del CreditReport. */
export type BdcLegalType = "natural" | "legal";

/** Body opcional de `bdc/start` (todos los campos son opcionales). */
export interface BdcStartRequest {
  /** Consentimiento T&C (evidencia local; NO viaja a mb_platform). */
  accept_tyc?: boolean;
  /** Consentimiento medios electrónicos (evidencia local). */
  accept_electronic_media?: boolean;
}

/** Contact-only review; never sends identity, address or approved form values. */
export interface BdcContactReviewRequest {
  email: string;
  phone_number: string;
  dial_code: BdcDialCode;
  nip_destination_reviewed: true;
}

export interface BdcContactReviewResponse {
  reviewed: boolean;
}

/** Body de `bdc/confirm-pin` (requerido). */
export interface BdcConfirmPinRequest {
  pin: string;
  confirm_pin: string;
}

/** Body de `bdc/resend-pin` (requerido). */
export interface BdcResendPinRequest {
  method: BdcResendMethod;
}

/**
 * Forma base de toda respuesta de los endpoints `start`/`confirm-pin`/`resend-pin`.
 *
 * `retry_pin` = 10 - pin_retry_attempts; `resend_pin` = 5 - pin_resent_attempts.
 * Ambos pueden venir `null` en un resume puro -> el front usa defaults (10 / 5).
 *
 * `code` aparece cuando un 200 trae un error en el body.
 */
export interface BdcStateResponse {
  status: BdcStatus | string;
  code?: string;
  masked_email?: string;
  email_matches_metadata?: boolean;
  masked_phone?: string;
  retry_pin: number | null;
  resend_pin: number | null;
  requirement_status?: string;
  /** Ambiguous Start outcome; only status reads can recover the current report. */
  start_recovery_pending?: boolean;
}

/** Respuesta de `GET bdc/status` (read-only): añade metadatos de rehidratación. */
export interface BdcStatusResponse extends BdcStateResponse {
  is_active?: boolean;
  is_native_bdc_run?: boolean;
  legal_type?: BdcLegalType | string;
  dial_code?: BdcDialCode;
  sic_source?: string | null;
  /** Read-only eligibility; the contact-review POST performs recovery. */
  identity_recovery_eligible?: boolean;
  /** Rejected legacy authorization whose current report still accepts capture. */
  legacy_capture_recovery_eligible?: boolean;
}

export interface BdcCompanyDataValues {
  business_legal_name: string;
  rfc_company: string;
  street: string;
  exterior_number: string;
  interior_number: string;
  postal_code: string;
  neighborhood: string;
  municipality: string;
  state: string;
}

export const BDC_COMPANY_DATA_KEYS: readonly (keyof BdcCompanyDataValues)[] = [
  "business_legal_name",
  "rfc_company",
  "street",
  "exterior_number",
  "interior_number",
  "postal_code",
  "neighborhood",
  "municipality",
  "state",
];

export interface BdcCompanyDataResponse {
  values: BdcCompanyDataValues;
  reviewed: boolean;
  locked?: boolean;
  source: "business-form" | "participant-confirmed" | string;
  reviewed_at?: string | null;
}

const bdcBasePath = (requirementId: string) =>
  `/docvault/requirements/${requirementId}/bdc`;

const createBdcApi = (client: AxiosInstance) => ({
  confirmContact: (requirementId: string, body: BdcContactReviewRequest) =>
    client
      .post<BdcContactReviewResponse>(
        `${bdcBasePath(requirementId)}/contact-review`,
        body,
      )
      .then((r) => r.data),

  /**
   * POST bdc/start — resuelve el credit report y DISPARA el NIP cuando el
   * estado es `capturing_inputs` (idempotente: si ya está en `pin_validation`
   * no re-dispara). Debe llamarse SOLO por acción explícita del usuario.
   */
  start: (requirementId: string, body: BdcStartRequest = {}) =>
    client
      .post<BdcStateResponse>(`${bdcBasePath(requirementId)}/start`, body)
      .then((r) => r.data),

  /**
   * POST bdc/confirm-pin — confirma el NIP. El estado `completed` indica que
   * la consulta SIC terminó; Platform actualiza el requisito mediante el
   * endpoint de estado existente.
   */
  confirmPin: (requirementId: string, body: BdcConfirmPinRequest) =>
    client
      .post<BdcStateResponse>(`${bdcBasePath(requirementId)}/confirm-pin`, body)
      .then((r) => r.data),

  /** POST bdc/resend-pin — reenvía el NIP por Email o SMS. */
  resendPin: (requirementId: string, body: BdcResendPinRequest) =>
    client
      .post<BdcStateResponse>(`${bdcBasePath(requirementId)}/resend-pin`, body)
      .then((r) => r.data),

  /** GET bdc/status — read-only, NO dispara NIP. Útil para resume tras recarga. */
  getStatus: (requirementId: string) =>
    client
      .get<BdcStatusResponse>(`${bdcBasePath(requirementId)}/status`)
      .then((r) => r.data),

  getCompanyData: (requirementId: string, signal?: AbortSignal) =>
    client
      .get<BdcCompanyDataResponse>(
        `${bdcBasePath(requirementId)}/company-data`,
        {
          signal,
        },
      )
      .then((r) => r.data),

  confirmCompanyData: (requirementId: string, values: BdcCompanyDataValues) =>
    client
      .put<BdcCompanyDataResponse>(
        `${bdcBasePath(requirementId)}/company-data`,
        { values },
      )
      .then((r) => r.data),
});

/**
 * Cliente BDC actor: todas las mutaciones y lecturas desde `/vault` usan el
 * JWT del actor.
 */
export const bdcApi = createBdcApi(vaultActorApi);

/**
 * Cliente BDC con token de sesión staff: sólo debe usarse para consultas read-only desde
 * `/folders`. El orquestador no expone acciones de start/confirm/resend
 * cuando se monta en este contexto.
 */
const staffBdcClient = createBdcApi(staffApi);
export const staffBdcApi = {
  getStatus: staffBdcClient.getStatus,
  getCompanyData: staffBdcClient.getCompanyData,
};
